import { map } from "lodash";
import { aiccInstance } from "utils/instance";
import {
  AiccAvailableLanguageResponse,
  AiccLanguageContentVersionsResponse,
  AiccLanguageListItemResponse,
  AiccLanguageListResponse,
} from "types/dto/response/aiccLanguage";
import { PublishAiccLanguageRequest } from "types/dto/request/aiccLanguage";
import {
  transformAiccAvailableLanguage,
  transformAiccLanguageContentVersions,
  transformAiccManagedLanguage,
  transformAiccManagedLanguageList,
} from "types/transformers/aiccLanguage";

/**
 * Language management (AICC2-327 / 331 / 335), against `{AICC_API_BASE_URL}/languages`.
 * The base URL already carries `/api/admin`, so these paths are relative to it.
 *
 * Every mutation returns the updated language, so a caller can refresh one row from the
 * response instead of re-fetching the list.
 *
 * Three things worth knowing before adding to this file:
 *
 * - **Content edits do not live here.** Each of the 14 sub-assets has its own long-standing
 *   endpoint (vocabulary CSV, lesson-info seed, prompt flows…), and the status side effect
 *   of editing one — releasing the publish-ready lock — is applied server-side on all 28
 *   write paths. The contract's single "save an asset" endpoint was never implemented and
 *   is not needed.
 * - **A working copy is the precondition for editing.** Published and archived versions are
 *   immutable; marking or saving anything without a copy answers 409.
 * - **409 is not a failure.** The request was valid and the service is healthy; the language
 *   just does not accept that action yet. See `AICC_LANGUAGE_CONFLICT` in the feature's
 *   configs for what each one means.
 */

/**
 * Whatever is asked for without the fourteen asset breakdowns.
 *
 * Computing one language's breakdown walks its content partitions and costs the backend ~1.4s, and
 * the list paid it per row: five languages took 4.86s and it grew linearly. Everything else in the
 * response — status, release history, `next_version` — is a plain lookup and still comes back, so a
 * collapsed card is fully answerable; the copies are read per language when one is expanded.
 */
const WITHOUT_ASSETS = { params: { assets: false } };

/** Languages under AICC management, soft-deleted ones excluded. */
export async function fetchAiccManagedLanguages() {
  return aiccInstance
    .get<AiccLanguageListResponse>("/languages", WITHOUT_ASSETS)
    .then((response) => transformAiccManagedLanguageList(response));
}

/**
 * Every readable copy of one language's content, each with its fourteen assets. This is the request
 * the list no longer makes for all of them at once — one card's worth, when that card is opened.
 */
export async function fetchAiccLanguageContentVersions(languageId: number) {
  return aiccInstance
    .get<AiccLanguageContentVersionsResponse>(`/languages/${languageId}/versions`)
    .then((response) => transformAiccLanguageContentVersions(response));
}

/**
 * One managed language, in the same shape as a list item. This is how a row is refreshed after a
 * change: a mutation's own response says what it did, but re-reading is what guarantees the row
 * matches what the next reader of the list would see.
 */
export async function fetchAiccManagedLanguage(languageId: number) {
  return aiccInstance
    .get<AiccLanguageListItemResponse>(`/languages/${languageId}/managed`)
    .then((response) => transformAiccManagedLanguage(response));
}

/** The global library minus anything already managed; soft-deleted ones reappear here. */
export async function fetchAiccAvailableLanguages() {
  return aiccInstance
    .get<AiccAvailableLanguageResponse[]>("/languages/available")
    .then((response) => map(response ?? [], transformAiccAvailableLanguage));
}

/** Soft delete: sets the marker and keeps the content rows, live version and working copy. */
export async function deleteAiccLanguage(languageId: number) {
  return aiccInstance.delete<void>(`/languages/${languageId}`);
}

/**
 * An empty working copy, which is also how a language is added: this takes it under management on
 * the way, so `POST /languages` — the backend's own entry point for that — is not called from here.
 * A language with no working copy answers 409 to every content edit, so the two are one action as
 * far as the portal is concerned.
 */
export async function createAiccWorkingCopy(languageId: number) {
  return aiccInstance
    .post<AiccLanguageListItemResponse>(`/languages/${languageId}/create`, undefined, WITHOUT_ASSETS)
    .then((response) => transformAiccManagedLanguage(response));
}

/**
 * Duplicate Preview: clones the live version into a working copy at preview-ready. The live
 * version keeps serving standard end users; only test accounts see the copy.
 *
 * `overwrite` discards an existing copy, which the caller must confirm first — the backend
 * otherwise answers 409 rather than silently dropping unpublished edits.
 */
export async function createAiccPreview(languageId: number, overwrite = false) {
  return aiccInstance
    .post<AiccLanguageListItemResponse>(`/languages/${languageId}/preview`, undefined, {
      params: { ...WITHOUT_ASSETS.params, ...(overwrite ? { overwrite: true } : undefined) },
    })
    .then((response) => transformAiccManagedLanguage(response));
}

/**
 * Restore: clones an earlier version into the working copy at preview-ready. This does not
 * go live — publishing the copy afterwards mints the next number, so restoring V1 over a
 * live V2 eventually produces V3 and history stays append-only.
 */
export async function restoreAiccVersion(languageId: number, version: number, overwrite = true) {
  return aiccInstance
    .post<AiccLanguageListItemResponse>(`/languages/${languageId}/restore/${version}`, undefined, {
      params: { ...WITHOUT_ASSETS.params, ...(overwrite ? { overwrite: true } : undefined) },
    })
    .then((response) => transformAiccManagedLanguage(response));
}

/** Locks the working copy as an end-to-end tested combination. Does not publish it.
 *
 * `WITHOUT_ASSETS` for the same reason as the mark above, and here nothing at all is read off the
 * response — the caller discards it and refreshes.
 */
export async function markAiccLanguageReadyToPublish(languageId: number) {
  return aiccInstance
    .put<AiccLanguageListItemResponse>(`/languages/${languageId}/publish-ready`, undefined, WITHOUT_ASSETS)
    .then((response) => transformAiccManagedLanguage(response));
}

/**
 * Freezes the working copy as a new version and makes it live, archiving the version it
 * replaces. Release notes are required — the history is the only audit trail there is.
 * Withdrawing and republishing without an edit keeps the original number.
 */
export async function publishAiccLanguage(languageId: number, releaseNotes: string) {
  return aiccInstance
    .post<AiccLanguageListItemResponse>(`/languages/${languageId}/publish`, {
      release_notes: releaseNotes,
    } as PublishAiccLanguageRequest)
    .then((response) => transformAiccManagedLanguage(response));
}
